import React, { useState, useCallback, useMemo } from "react";
import axios from "axios";
import { DATE_RANGE_OPTIONS, getDateRangePreset } from "./dateRangeUtils";
import WorkstationSelect from "./WorkstationSelect";
import {
  Alert, Box, Card, Chip, CircularProgress, Collapse,
  Divider, Fade, FormControl, Grid, IconButton, InputAdornment, MenuItem, Select,
  Table, TableBody, TableCell, TableContainer, TableRow,
  TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import Button from "@mui/material/Button";
import { T } from "./adminTheme";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import DensitySmallIcon from "@mui/icons-material/DensitySmall";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";

import {
  SectionCard, StyledTableHead, StyledTableRow, FilterField,
  TouchTooltip, formatName, splitNames, getRowKey,
  parseHHMMSSToMinutes, CACHE_DURATION_MS,
} from "./shared";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function RecentShiftsTab({
  onSnack,
  endpoint,
  allocatedMachines = null,
  jobSummaryByShiftEndpoint,
  operationLogByShiftEndpoint,
}) {
  const theme  = useTheme();
  const t      = T(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  // ── filter / view state ───────────────────────────────────────────────────
  const defaultRange = getDateRangePreset("this_week");
  const [datePreset, setDatePreset] = useState("this_week");
  const [filters, setFilters] = useState({ machineCode: "", employeeId: "", activeStatus: "", startDate: defaultRange.startDate, endDate: defaultRange.endDate });
  const [shiftLogs, setShiftLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dense, setDense] = useState(false);

  // ── per-shift expand state ────────────────────────────────────────────────
  const [expandedShift, setExpandedShift] = useState({});
  const [jobSectionOpen, setJobSectionOpen] = useState({});
  const [opSectionOpen, setOpSectionOpen] = useState({});

  // ── per-shift lazy data ───────────────────────────────────────────────────
  const [jobSummaryCache, setJobSummaryCache] = useState({});
  const [opLogCache, setOpLogCache] = useState({});
  const [cacheTimestamp, setCacheTimestamp] = useState({});
  const [jobLoading, setJobLoading] = useState({});
  const [opLoading, setOpLoading] = useState({});
  const [jobError, setJobError] = useState({});
  const [opError, setOpError] = useState({});

  // ── helpers ───────────────────────────────────────────────────────────────
  const formatDateTime = useCallback((dateString) => {
    if (!dateString) return "—";
    try {
      return new Date(dateString).toLocaleString("en-US", {
        year: "numeric", month: "short", day: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: true,
      });
    } catch { return String(dateString); }
  }, []);

  const sumMinutes = useCallback((jobs, field) =>
    (jobs || []).reduce((acc, cur) => acc + parseHHMMSSToMinutes(cur?.[field]), 0), []);
  const sumNumber = useCallback((jobs, field) =>
    (jobs || []).reduce((acc, cur) => acc + Number(cur?.[field] || 0), 0), []);
  const sumOpDuration = useCallback((ops) =>
    (ops || []).reduce((acc, cur) => acc + Number(cur?.duration_minutes || 0), 0), []);
  const sumOpProduction = useCallback((ops) =>
    (ops || []).reduce((acc, cur) => {
      const t = String(cur?.operation_type || "").toLowerCase();
      return acc + (t === "production" ? Number(cur?.duration_minutes || 0) : 0);
    }, 0), []);

  // ── main search ───────────────────────────────────────────────────────────
  const fetchShifts = useCallback(async () => {
    setLoading(true);
    try {
      const searchEndpoint = endpoint || `${API_BASE_URL}/admin/shiftlogs/search`;
      const res = await axios.post(searchEndpoint, {
        machineCode: filters.machineCode || null,
        employeeId: filters.employeeId || null,
        activeStatus: filters.activeStatus || null,
        startDate: filters.startDate || null,
        endDate: filters.endDate || null,
      });
      setShiftLogs(res.data || []);
      setExpandedShift({});
      setJobSectionOpen({});
      setOpSectionOpen({});
      setJobSummaryCache({});
      setOpLogCache({});
      setCacheTimestamp({});
      setJobLoading({});
      setOpLoading({});
      setJobError({});
      setOpError({});
    } catch (err) {
      onSnack(err?.response?.data?.message || "Failed to load shift logs.", "error");
    } finally {
      setLoading(false);
    }
  }, [endpoint, filters, onSnack]);

  // ── lazy job summary fetch ────────────────────────────────────────────────
  const fetchJobSummaryForShift = useCallback(async (row) => {
    const rowKey = getRowKey(row);
    const cacheAge = Date.now() - (cacheTimestamp[rowKey] || 0);
    if (cacheAge < CACHE_DURATION_MS && Array.isArray(jobSummaryCache[rowKey])) return;

    setJobLoading((prev) => ({ ...prev, [rowKey]: true }));
    setJobError((prev) => ({ ...prev, [rowKey]: "" }));

    try {
      const endpointUrl = jobSummaryByShiftEndpoint || `${API_BASE_URL}/jobsummary/by-shift`;
      const resp = await axios.get(endpointUrl, {
        params: { machineCode: row.machineCode, shiftIn: row.shift_loggedIn_DateTime, shiftOut: row.shift_loggedOut_DateTime ?? null, employeeId: row.Employee_ID || null },
        paramsSerializer: (p) => {
          const usp = new URLSearchParams();
          Object.entries(p).forEach(([k, v]) => { if (v !== null && v !== undefined) usp.append(k, v); });
          return usp.toString();
        },
      });
      const raw = Array.isArray(resp.data) ? resp.data : [];
      const sorted = raw.slice().sort((a, b) => {
        const am = a?.startDatetime ? new Date(a.startDatetime).getTime() : -Infinity;
        const bm = b?.startDatetime ? new Date(b.startDatetime).getTime() : -Infinity;
        return bm - am;
      });
      setJobSummaryCache((prev) => ({ ...prev, [rowKey]: sorted }));
      setCacheTimestamp((prev) => ({ ...prev, [rowKey]: Date.now() }));
    } catch {
      setJobError((prev) => ({ ...prev, [rowKey]: "Failed to load job summary" }));
    } finally {
      setJobLoading((prev) => ({ ...prev, [rowKey]: false }));
    }
  }, [jobSummaryByShiftEndpoint, jobSummaryCache, cacheTimestamp]);

  // ── lazy op log fetch ─────────────────────────────────────────────────────
  const fetchOpLogForShift = useCallback(async (row) => {
    const rowKey = getRowKey(row);
    const cacheAge = Date.now() - (cacheTimestamp[rowKey + "_oplog"] || 0);
    if (cacheAge < CACHE_DURATION_MS && Array.isArray(opLogCache[rowKey])) return;

    setOpLoading((prev) => ({ ...prev, [rowKey]: true }));
    setOpError((prev) => ({ ...prev, [rowKey]: "" }));

    try {
      const endpointUrl = operationLogByShiftEndpoint || `${API_BASE_URL}/operationlog/by-shift`;
      const resp = await axios.get(endpointUrl, {
        params: { machineCode: row.machineCode, shiftIn: row.shift_loggedIn_DateTime, shiftOut: row.shift_loggedOut_DateTime ?? null, employeeId: row.Employee_ID || null },
        paramsSerializer: (p) => {
          const usp = new URLSearchParams();
          Object.entries(p).forEach(([k, v]) => { if (v !== null && v !== undefined) usp.append(k, v); });
          return usp.toString();
        },
      });
      const raw = Array.isArray(resp.data) ? resp.data : [];
      const pickTime = (r) => {
        const ed = r?.end_datetime ? new Date(r.end_datetime).getTime() : -Infinity;
        const sd = r?.start_datetime ? new Date(r.start_datetime).getTime() : -Infinity;
        return Number.isFinite(ed) ? ed : sd;
      };
      const sorted = raw.slice().sort((a, b) => pickTime(b) - pickTime(a));
      setOpLogCache((prev) => ({ ...prev, [rowKey]: sorted }));
      setCacheTimestamp((prev) => ({ ...prev, [rowKey + "_oplog"]: Date.now() }));
    } catch {
      setOpError((prev) => ({ ...prev, [rowKey]: "Failed to load operation log" }));
    } finally {
      setOpLoading((prev) => ({ ...prev, [rowKey]: false }));
    }
  }, [operationLogByShiftEndpoint, opLogCache, cacheTimestamp]);

  // ── expand handlers ───────────────────────────────────────────────────────
  const handleRowExpand = useCallback((row) => {
    const rowKey = getRowKey(row);
    setExpandedShift((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }));
  }, []);

  const toggleJobSection = useCallback((row) => {
    const rowKey = getRowKey(row);
    const willOpen = !jobSectionOpen[rowKey];
    setJobSectionOpen((prev) => ({ ...prev, [rowKey]: willOpen }));
    if (willOpen) fetchJobSummaryForShift(row);
  }, [jobSectionOpen, fetchJobSummaryForShift]);

  const toggleOpSection = useCallback((row) => {
    const rowKey = getRowKey(row);
    const willOpen = !opSectionOpen[rowKey];
    setOpSectionOpen((prev) => ({ ...prev, [rowKey]: willOpen }));
    if (willOpen) fetchOpLogForShift(row);
  }, [opSectionOpen, fetchOpLogForShift]);

  // ── filtered list ─────────────────────────────────────────────────────────
  const filteredShifts = useMemo(() => {
    let list = shiftLogs;
    if (statusFilter === "in") list = list.filter((r) => r.ActiveStatus === "Logged-IN");
    else if (statusFilter === "out") list = list.filter((r) => r.ActiveStatus === "Logged-out");
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r) =>
        (r.Employee_ID && String(r.Employee_ID).toLowerCase().includes(q)) ||
        (r.operator && r.operator.toLowerCase().includes(q)) ||
        (r.machineCode && r.machineCode.toLowerCase().includes(q))
      );
    }
    return list;
  }, [shiftLogs, statusFilter, search]);

  // ── section header sub-component ─────────────────────────────────────────
  const SectionHeader = ({ title, count, rightChips, isOpen, onToggle }) => (
    <Box
      sx={{ py: 1, px: 1.5, borderRadius: 1, bgcolor: t.s2, border: `0.5px solid ${t.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
      onClick={onToggle}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <IconButton size="small">{isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
        <Chip size="small" label={`${count} item${count !== 1 ? "s" : ""}`} />
      </Box>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>{rightChips}</Box>
    </Box>
  );

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <Fade in>
      <Box sx={{ maxWidth: "100%", overflow: "hidden" }}>
        <SectionCard>
          {/* toolbar */}
          <Box sx={{ px: 3, pb: 2 }}>
            <Typography sx={{ fontSize: 17, fontWeight: 400, color: t.heading }}>Search shifts and drill into job summaries &amp; operation logs</Typography>
          </Box>
          <Divider />

          {/* search filters */}
          <Box sx={{ px: 3, pt: 2, pb: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4} lg={2.4} xl={2}>
                <WorkstationSelect
                  label="Workstation"
                  value={filters.machineCode}
                  allocatedMachines={allocatedMachines}
                  allLabel={allocatedMachines ? "All Allocated Machines" : "All Workstations"}
                  onChange={(value) => setFilters((f) => ({ ...f, machineCode: value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2.4} xl={2}>
                <FilterField label="Employee ID" value={filters.employeeId} onChange={(e) => setFilters((f) => ({ ...f, employeeId: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2.4} xl={2}>
                <FilterField label="Active Status" value={filters.activeStatus} onChange={(e) => setFilters((f) => ({ ...f, activeStatus: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2.4} xl={2}>
                <FormControl fullWidth size="small">
                  <Select
                    value={datePreset}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDatePreset(value);
                      if (value !== "custom") {
                        const range = getDateRangePreset(value);
                        setFilters((f) => ({ ...f, startDate: range.startDate, endDate: range.endDate }));
                      }
                    }}
                  >
                    {DATE_RANGE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2.4} xl={2}>
                <FilterField label="Start Date" type="datetime-local"
                  value={filters.startDate ? filters.startDate.replace(" ", "T") : ""}
                  onChange={(e) => {
                    setDatePreset("custom");
                    setFilters((f) => ({ ...f, startDate: e.target.value ? e.target.value.replace("T", " ") : "" }));
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2.4} xl={2}>
                <FilterField label="End Date" type="datetime-local"
                  value={filters.endDate ? filters.endDate.replace(" ", "T") : ""}
                  onChange={(e) => {
                    setDatePreset("custom");
                    setFilters((f) => ({ ...f, endDate: e.target.value ? e.target.value.replace("T", " ") : "" }));
                  }}
                />
              </Grid>
            </Grid>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button variant="contained" onClick={fetchShifts} disabled={loading}
                startIcon={loading ? <CircularProgress size={15} color="inherit" /> : <SearchIcon />}
                sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}>
                {loading ? "Loading..." : "Search Shifts"}
              </Button>
            </Box>
          </Box>

          {/* inline search + status filter */}
          {shiftLogs.length > 0 && (
            <Box sx={{ px: 3, py: 1, borderTop: `0.5px solid ${t.border}`, display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
              <TextField
                placeholder="Filter by ID, name, machine…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                  endAdornment: search && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch("")}><ClearIcon fontSize="small" /></IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 220, flex: 1 }}
              />
              <Chip label={`${filteredShifts.length} shift${filteredShifts.length !== 1 ? "s" : ""}`} size="small" variant="outlined" />
              <ToggleButtonGroup size="small" exclusive value={statusFilter} onChange={(_, v) => v && setStatusFilter(v)}>
                <ToggleButton value="all"><FilterListIcon sx={{ mr: 0.5 }} fontSize="small" />All</ToggleButton>
                <ToggleButton value="in">Logged-IN</ToggleButton>
                <ToggleButton value="out">Logged-out</ToggleButton>
              </ToggleButtonGroup>
              <Divider orientation="vertical" flexItem />
              <Tooltip title={dense ? "Comfortable spacing" : "Compact density"}>
                <IconButton size="small" onClick={() => setDense((d) => !d)}><DensitySmallIcon fontSize="small" /></IconButton>
              </Tooltip>
              <Tooltip title="Re-fetch">
                <IconButton size="small" onClick={fetchShifts} disabled={loading}>
                  {loading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {/* states */}
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1.5, py: 6 }}>
              <CircularProgress size={30} /><Typography color="text.secondary">Loading shift logs…</Typography>
            </Box>
          )}
          {!loading && shiftLogs.length > 0 && filteredShifts.length === 0 && (
            <Box sx={{ py: 6, textAlign: "center" }}><Typography color="text.secondary">No shifts match your filter.</Typography></Box>
          )}
          {!loading && shiftLogs.length === 0 && (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Typography color="text.secondary">Use the filters above and click "Search Shifts" to load data.</Typography>
            </Box>
          )}

          {/* shift cards */}
          {!loading && filteredShifts.length > 0 && (
            <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
              {filteredShifts.map((row) => {
                const rowKey = getRowKey(row);
                const isExpanded = !!expandedShift[rowKey];
                const isJobOpen = !!jobSectionOpen[rowKey];
                const isOpOpen = !!opSectionOpen[rowKey];
                const isActive = row.ActiveStatus === "Logged-IN";

                const jobCache = jobSummaryCache[rowKey] || [];
                const opCache = opLogCache[rowKey] || [];
                const jobLoadingState = !!jobLoading[rowKey];
                const opLoadingState = !!opLoading[rowKey];
                const jobErr = jobError[rowKey] || "";
                const opErr = opError[rowKey] || "";

                const totalProdMin = sumMinutes(jobCache, "totalProductionTime");
                const totalSetupMin = sumMinutes(jobCache, "totalSetupTime");
                const totalIdleMin = sumMinutes(jobCache, "totalIdleTime");
                const totalMealsMin = sumMinutes(jobCache, "totalMealsTime");
                const totalProdQty = sumNumber(jobCache, "productionQty");
                const totalWasteQty = sumNumber(jobCache, "wasteQty");
                const totalOpMin = sumOpDuration(opCache);
                const totalOpProdMin = sumOpProduction(opCache);

                return (
                  <Card
                    key={rowKey}
                    elevation={isExpanded ? 3 : 1}
                    sx={{
                      borderLeft: isActive ? `4px solid ${t.green}` : `4px solid ${t.muted}`,
                      borderRadius: 2,
                      transition: "all 0.2s ease",
                      "&:hover": { boxShadow: 4 },
                    }}
                  >
                    {/* card header */}
                    <Box
                      onClick={() => handleRowExpand(row)}
                      sx={{
                        px: 2, py: 1.5, cursor: "pointer",
                        bgcolor: isActive ? alpha(t.green, isDark ? 0.10 : 0.06) : alpha(t.muted, isDark ? 0.06 : 0.04),
                        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2,
                        "&:hover": { bgcolor: isActive ? alpha(t.green, isDark ? 0.16 : 0.10) : alpha(t.muted, isDark ? 0.10 : 0.08) },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
                        <IconButton size="small" sx={{ p: 0.5 }}>
                          {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                        </IconButton>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0, flex: 1 }}>
                          {/* operators row */}
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                              <TouchTooltip title={row.operator || "Unknown Operator"}>
                                <Chip size="small" label={formatName(row.operator || "Unknown")} color="primary" sx={{ fontWeight: 600 }} />
                              </TouchTooltip>
                              {splitNames(row.AstOperator).map((name, idx) => (
                                <TouchTooltip key={`ast-${idx}`} title={name}>
                                  <Chip size="small" label={`Ast: ${formatName(name)}`} variant="outlined" color="info" />
                                </TouchTooltip>
                              ))}
                              {splitNames(row.Helpers).map((name, idx) => (
                                <TouchTooltip key={`helper-${idx}`} title={name}>
                                  <Chip size="small" label={`Helper: ${formatName(name)}`} variant="outlined" color="default" />
                                </TouchTooltip>
                              ))}
                            </Box>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>({row.Employee_ID || "—"})</Typography>
                            <Chip size="small" label={row.ActiveStatus || "—"}
                              color={isActive ? "success" : "default"} variant={isActive ? "filled" : "outlined"}
                              sx={{ fontWeight: 600, height: 20, fontSize: "0.7rem" }} />
                          </Box>
                          {/* times row */}
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <AccessTimeIcon sx={{ fontSize: 14, color: "success.main" }} />
                              <Typography variant="caption" sx={{ fontWeight: 600, color: "success.main" }}>IN:</Typography>
                              <Typography variant="caption">{formatDateTime(row.shift_loggedIn_DateTime)}</Typography>
                            </Box>
                            <Typography variant="caption" sx={{ color: "text.disabled" }}>|</Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <AccessTimeIcon sx={{ fontSize: 14, color: row.shift_loggedOut_DateTime ? "error.main" : "text.disabled" }} />
                              <Typography variant="caption" sx={{ fontWeight: 600, color: row.shift_loggedOut_DateTime ? "error.main" : "text.disabled" }}>OUT:</Typography>
                              <Typography variant="caption" sx={{ color: row.shift_loggedOut_DateTime ? "text.primary" : "text.disabled" }}>
                                {row.shift_loggedOut_DateTime ? formatDateTime(row.shift_loggedOut_DateTime) : "Still Active"}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                      {/* machine chip */}
                      <Chip icon={<PrecisionManufacturingIcon sx={{ fontSize: 16 }} />} label={row.machineCode || "—"}
                        variant="outlined" color="primary" size="small" sx={{ fontWeight: 600, height: 26 }} />
                    </Box>

                    {/* expanded content */}
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ bgcolor: t.s1, borderTop: `0.5px solid ${t.border}` }}>

                        {/* shift details */}
                        <Box sx={{ p: 2, borderBottom: `0.5px solid ${t.border}` }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: t.blue }}>Shift Details</Typography>
                          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 2 }}>
                            <Box><Typography variant="caption" color="text.secondary">Employee ID</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{row.Employee_ID || "—"}</Typography></Box>
                            <Box><Typography variant="caption" color="text.secondary">Operator</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{row.operator || "—"}</Typography></Box>
                            <Box><Typography variant="caption" color="text.secondary">Machine</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{row.machineCode || "—"}</Typography></Box>
                            <Box><Typography variant="caption" color="text.secondary">Shift In</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDateTime(row.shift_loggedIn_DateTime)}</Typography></Box>
                            <Box><Typography variant="caption" color="text.secondary">Shift Out</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{row.shift_loggedOut_DateTime ? formatDateTime(row.shift_loggedOut_DateTime) : "—"}</Typography></Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">Status</Typography>
                              <Box><Chip size="small" label={row.ActiveStatus || "—"} color={isActive ? "success" : "default"} variant={isActive ? "filled" : "outlined"} /></Box>
                            </Box>
                            {row.remarks && (
                              <Box sx={{ gridColumn: "1 / -1" }}>
                                <Typography variant="caption" color="text.secondary">Remarks</Typography>
                                <Typography variant="body2">{row.remarks}</Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>

                        {/* job summary sub-section */}
                        <Box sx={{ p: 2 }}>
                          <SectionHeader
                            title="Job Summary"
                            count={jobCache.length}
                            isOpen={isJobOpen}
                            onToggle={(e) => { e.stopPropagation(); toggleJobSection(row); }}
                            rightChips={<>
                              <Chip size="small" color="primary" label={`Prod min: ${totalProdMin}`} />
                              <Chip size="small" label={`Setup: ${totalSetupMin}`} />
                              <Chip size="small" label={`Idle: ${totalIdleMin}`} />
                              <Chip size="small" label={`Meals: ${totalMealsMin}`} />
                              <Chip size="small" color="success" label={`Prod Qty: ${totalProdQty}`} />
                              <Chip size="small" color="error" label={`Waste: ${totalWasteQty}`} />
                            </>}
                          />
                          <Collapse in={isJobOpen} timeout="auto" unmountOnExit>
                            <Box sx={{ mt: 1 }}>
                              {jobLoadingState && (
                                <Box display="flex" alignItems="center" gap={1} sx={{ p: 2 }}>
                                  <CircularProgress size={18} /><Typography variant="body2">Loading jobs…</Typography>
                                </Box>
                              )}
                              {jobErr && <Alert severity="error" sx={{ my: 1 }}>{jobErr}</Alert>}
                              {!jobLoadingState && !jobErr && jobCache.length === 0 && (
                                <Box sx={{ p: 2, color: "text.secondary" }}>No jobs found for this shift.</Box>
                              )}
                              {!jobLoadingState && !jobErr && jobCache.length > 0 && (
                                <TableContainer sx={{ overflowX: "auto" }}>
                                  <Table size={dense ? "small" : "medium"} sx={{ minWidth: 1800 }}>
                                    <StyledTableHead>
                                      <TableRow>
                                        {["Started","Ended","Job #","Customer","Product","Prod Qty","Waste Qty","Finished Good","Close Remarks","Setup Time","Idle Time","Production Time","Meals Time","Idle Reasons","Status","Avg Prod Speed","Avg Op Speed"].map((h) => (
                                          <TableCell key={h}>{h}</TableCell>
                                        ))}
                                      </TableRow>
                                    </StyledTableHead>
                                    <TableBody>
                                      {jobCache.map((js, i) => (
                                        <StyledTableRow key={`${rowKey}-job-${i}`}>
                                          <TableCell>{formatDateTime(js.startDatetime)}</TableCell>
                                          <TableCell>{js.endDatetime ? formatDateTime(js.endDatetime) : "—"}</TableCell>
                                          <TableCell sx={{ fontWeight: 700 }}>{js.jobNo || "—"}</TableCell>
                                          <TableCell>{js.customer || "—"}</TableCell>
                                          <TableCell>{js.product || "—"}</TableCell>
                                          <TableCell align="right">{js.productionQty || 0}</TableCell>
                                          <TableCell align="right">{js.wasteQty || 0}</TableCell>
                                          <TableCell>{js.finishedGood || "—"}</TableCell>
                                          <TableCell sx={{ maxWidth: 300, whiteSpace: "normal", wordBreak: "break-word" }}>{js.jobCloseremarks || "—"}</TableCell>
                                          <TableCell>{js.totalSetupTime || "00:00:00"}</TableCell>
                                          <TableCell>{js.totalIdleTime || "00:00:00"}</TableCell>
                                          <TableCell>{js.totalProductionTime || "00:00:00"}</TableCell>
                                          <TableCell>{js.totalMealsTime || "00:00:00"}</TableCell>
                                          <TableCell sx={{ maxWidth: 300, whiteSpace: "normal", wordBreak: "break-word" }}>{js.idleReasons || "—"}</TableCell>
                                          <TableCell><Chip label={js.status || "—"} size="small" color={String(js.status).toLowerCase() === "finished" ? "success" : "default"} /></TableCell>
                                          <TableCell align="right">{js.avg_prod_speed != null ? Number(js.avg_prod_speed).toFixed(2) : "0.00"}</TableCell>
                                          <TableCell align="right">{js.avg_op_speed != null ? Number(js.avg_op_speed).toFixed(2) : "0.00"}</TableCell>
                                        </StyledTableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              )}
                            </Box>
                          </Collapse>

                          {/* op log sub-section */}
                          <Box sx={{ mt: 2 }}>
                            <SectionHeader
                              title="Operation Logs"
                              count={opCache.length}
                              isOpen={isOpOpen}
                              onToggle={(e) => { e.stopPropagation(); toggleOpSection(row); }}
                              rightChips={<>
                                <Chip size="small" color="primary" label={`Prod min: ${totalOpProdMin}`} />
                                <Chip size="small" label={`Total min: ${totalOpMin}`} />
                              </>}
                            />
                            <Collapse in={isOpOpen} timeout="auto" unmountOnExit>
                              <Box sx={{ mt: 1 }}>
                                {opLoadingState && (
                                  <Box display="flex" alignItems="center" gap={1} sx={{ p: 2 }}>
                                    <CircularProgress size={18} /><Typography variant="body2">Loading operations…</Typography>
                                  </Box>
                                )}
                                {opErr && <Alert severity="error" sx={{ my: 1 }}>{opErr}</Alert>}
                                {!opLoadingState && !opErr && opCache.length === 0 && (
                                  <Box sx={{ p: 2, color: "text.secondary" }}>No operations found for this shift.</Box>
                                )}
                                {!opLoadingState && !opErr && opCache.length > 0 && (
                                  <TableContainer sx={{ overflowX: "auto" }}>
                                    <Table size={dense ? "small" : "medium"} sx={{ minWidth: 1200 }}>
                                      <StyledTableHead>
                                        <TableRow>
                                          {["Start","End","Job No","Op Code","Description","Type","Status","Duration (min)"].map((h) => (
                                            <TableCell key={h}>{h}</TableCell>
                                          ))}
                                        </TableRow>
                                      </StyledTableHead>
                                      <TableBody>
                                        {opCache.map((op, i) => {
                                          const s = op?.start_datetime ? new Date(op.start_datetime) : null;
                                          const e = op?.end_datetime ? new Date(op.end_datetime) : null;
                                          const minutes = s && e
                                            ? Math.max(0, Math.round((e - s) / 60000))
                                            : (op?.duration_minutes || 0);
                                          return (
                                            <StyledTableRow key={`${rowKey}-op-${i}`}>
                                              <TableCell>{formatDateTime(op.start_datetime)}</TableCell>
                                              <TableCell>{op.end_datetime ? formatDateTime(op.end_datetime) : "—"}</TableCell>
                                              <TableCell>{op.job_number || "—"}</TableCell>
                                              <TableCell>{op.operation_code || "—"}</TableCell>
                                              <TableCell>{op.operation_description || "—"}</TableCell>
                                              <TableCell>{op.operation_type || "—"}</TableCell>
                                              <TableCell>{op.active_status || "—"}</TableCell>
                                              <TableCell align="right">{minutes} ({(minutes / 60).toFixed(2)} hrs)</TableCell>
                                            </StyledTableRow>
                                          );
                                        })}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                )}
                              </Box>
                            </Collapse>
                          </Box>
                        </Box>
                      </Box>
                    </Collapse>
                  </Card>
                );
              })}
            </Box>
          )}
        </SectionCard>
      </Box>
    </Fade>
  );
}